import type { SubscriptionPricingDetailsModel } from '../../types/generated/models/subscription-pricing-details-model'
import type { BillingDetailsModel } from '../../types/generated/models/billing-details-model'

/* ── State interface ── */

export interface SubscriptionCheckout {
  clientSecret: string | null
  stripePublishableKey: string | null
  amountExcludingTax: number
  taxAmount: number
  amountIncludingTax: number
  currency: string
  subscriptionId: string | null
  subscriptionStatus: string | null
  // Seller chosen by the server from the billing country (never selected in the browser)
  sellerKey: string | null
  sellerLegalName: string | null
  billingCountry: string | null
  // Identifies the request this checkout belongs to, so late responses for an earlier selection are ignored
  requestId: string | null
  loading: boolean
  processing: boolean
  success: boolean
  error: boolean | null
  errorMessage: string | null
}

export interface SubscriptionState {
  pricingDetails: SubscriptionPricingDetailsModel
  billingDetails: BillingDetailsModel
  selectedCurrency: string
  checkout: SubscriptionCheckout
  loading: boolean
  error: boolean | null
  errorMessage: string | null
}

export const GET_PRICING_DETAILS = 'GET_PRICING_DETAILS'
export const GET_PRICING_DETAILS_SUCCESS = 'GET_PRICING_DETAILS_SUCCESS'
export const GET_PRICING_DETAILS_FAIL = 'GET_PRICING_DETAILS_FAIL'

// New actions for unified wizard
export const UPDATE_BILLING_DETAILS = 'UPDATE_BILLING_DETAILS'
export const UPDATE_SELECTED_CURRENCY = 'UPDATE_SELECTED_CURRENCY'
export const INITIALIZE_SUBSCRIPTION_CHECKOUT = 'INITIALIZE_SUBSCRIPTION_CHECKOUT'
export const INITIALIZE_SUBSCRIPTION_CHECKOUT_SUCCESS = 'INITIALIZE_SUBSCRIPTION_CHECKOUT_SUCCESS'
export const INITIALIZE_SUBSCRIPTION_CHECKOUT_FAIL = 'INITIALIZE_SUBSCRIPTION_CHECKOUT_FAIL'
export const RESET_SUBSCRIPTION_CHECKOUT = 'RESET_SUBSCRIPTION_CHECKOUT'
export const SET_SUBSCRIPTION_PAYMENT_PROCESSING = 'SET_SUBSCRIPTION_PAYMENT_PROCESSING'
export const SET_SUBSCRIPTION_PAYMENT_SUCCESS = 'SET_SUBSCRIPTION_PAYMENT_SUCCESS'
export const SET_SUBSCRIPTION_PAYMENT_ERROR = 'SET_SUBSCRIPTION_PAYMENT_ERROR'

export const defaultSubscriptionState: SubscriptionState = {
  // Pricing details from backend
  pricingDetails: {
    activeDeviceCount: 0,
    deviceBreakdown: [],
    totalMonthlyPrice: 0,
    subscriptionTier: '1',
    billingDetails: null,
    availableCurrencies: []
  },

  // Billing details (editable by user)
  billingDetails: {
    email: '',
    name: '',
    companyName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'GB',
    vatNumber: ''
  },

  // Currency selection
  selectedCurrency: 'GBP',

  // Checkout state
  checkout: {
    clientSecret: null,
    stripePublishableKey: null,
    amountExcludingTax: 0,
    taxAmount: 0,
    amountIncludingTax: 0,
    currency: 'gbp',
    subscriptionId: null,
    subscriptionStatus: null,
    sellerKey: null,
    sellerLegalName: null,
    billingCountry: null,
    requestId: null,
    loading: false,
    processing: false,
    success: false,
    error: null,
    errorMessage: null
  },

  // General loading state
  loading: false,
  error: null,
  errorMessage: null
}

// A checkout belongs to the billing country and currency it was created for. Changing either
// discards the client secret, totals and seller, and detaches any request still in flight.
const discardCheckout = (state: SubscriptionState): SubscriptionCheckout =>
  state.checkout.clientSecret || state.checkout.loading || state.checkout.requestId || state.checkout.error
    ? defaultSubscriptionState.checkout
    : state.checkout

const isCurrentCheckoutRequest = (state: SubscriptionState, action: any): boolean => {
  const sent = action.meta?.previousAction?.payload?.request?.data?.clientRequestId ?? null
  return sent !== null && sent === state.checkout.requestId
}

const matchCheckoutCountry = (countries: any[] | null | undefined, value: string | null | undefined) => {
  const wanted = (value || '').trim().toLowerCase()
  if (!wanted) return undefined
  return (countries || []).find(
    (c: any) => (c.code || '').toLowerCase() === wanted || (c.name || '').toLowerCase() === wanted
  )
}

export default (state: SubscriptionState = defaultSubscriptionState, action: any): SubscriptionState => {
  switch (action.type) {
    case GET_PRICING_DETAILS:
      return {
        ...state,
        loading: true,
        error: false,
        errorMessage: null
      }

    case GET_PRICING_DETAILS_SUCCESS:
      const pricingData = action.payload.data || defaultSubscriptionState.pricingDetails
      // Initialize billing details from backend response
      const backendBillingDetails = pricingData.billingDetails || {}

      // Only countries the server currently sells to can be chosen. The organisation's saved
      // country is used when it is one of them; otherwise the user must pick one.
      const openCountry = matchCheckoutCountry(pricingData.checkoutCountries, backendBillingDetails?.country)
      const defaultCurrency = (openCountry?.defaultCurrency || 'GBP').toUpperCase()

      return {
        ...state,
        pricingDetails: pricingData,
        billingDetails: {
          ...defaultSubscriptionState.billingDetails,
          ...backendBillingDetails,
          country: openCountry?.code || ''
        },
        selectedCurrency: defaultCurrency,
        loading: false,
        error: false,
        errorMessage: null
      }

    case GET_PRICING_DETAILS_FAIL:
      return {
        ...state,
        loading: false,
        error: true,
        errorMessage: action.payload
      }

    case UPDATE_BILLING_DETAILS:
      return {
        ...state,
        billingDetails: {
          ...state.billingDetails,
          ...action.payload
        },
        checkout: discardCheckout(state)
      }

    case UPDATE_SELECTED_CURRENCY:
      return {
        ...state,
        selectedCurrency: action.payload,
        checkout: discardCheckout(state)
      }

    case INITIALIZE_SUBSCRIPTION_CHECKOUT:
      return {
        ...state,
        checkout: {
          ...defaultSubscriptionState.checkout,
          requestId: action.payload?.request?.data?.clientRequestId ?? null,
          loading: true
        }
      }

    case INITIALIZE_SUBSCRIPTION_CHECKOUT_SUCCESS:
      // A response for a request that is no longer current (the user moved on, changed country or
      // closed the wizard) must not replace the current checkout.
      if (!isCurrentCheckoutRequest(state, action)) return state
      const checkoutData = action.payload.data || {}
      return {
        ...state,
        checkout: {
          ...state.checkout,
          clientSecret: checkoutData.clientSecret,
          stripePublishableKey: checkoutData.stripePublishableKey,
          amountExcludingTax: checkoutData.amountExcludingTax || 0,
          taxAmount: checkoutData.taxAmount || 0,
          amountIncludingTax: checkoutData.amountIncludingTax || 0,
          currency: checkoutData.currency || 'gbp',
          subscriptionId: checkoutData.subscriptionId,
          subscriptionStatus: checkoutData.subscriptionStatus,
          sellerKey: checkoutData.sellerKey ?? null,
          sellerLegalName: checkoutData.sellerLegalName ?? null,
          billingCountry: checkoutData.billingCountry ?? null,
          loading: false,
          error: false,
          errorMessage: null
        }
      }

    case INITIALIZE_SUBSCRIPTION_CHECKOUT_FAIL:
      if (!isCurrentCheckoutRequest(state, action)) return state
      let errorMsg = 'Failed to initialize checkout'
      if (action.payload?.data?.error) {
        errorMsg = action.payload.data.error
      } else if (action.payload?.message) {
        errorMsg = action.payload.message
      } else if (typeof action.payload === 'string') {
        errorMsg = action.payload
      }
      return {
        ...state,
        checkout: {
          ...state.checkout,
          loading: false,
          error: true,
          errorMessage: errorMsg,
          clientSecret: null
        }
      }

    case RESET_SUBSCRIPTION_CHECKOUT:
      return {
        ...state,
        checkout: defaultSubscriptionState.checkout
      }

    case SET_SUBSCRIPTION_PAYMENT_PROCESSING:
      return {
        ...state,
        checkout: {
          ...state.checkout,
          processing: true,
          error: false,
          errorMessage: null
        }
      }

    case SET_SUBSCRIPTION_PAYMENT_SUCCESS:
      return {
        ...state,
        checkout: {
          ...state.checkout,
          processing: false,
          success: true,
          error: false,
          errorMessage: null
        }
      }

    case SET_SUBSCRIPTION_PAYMENT_ERROR:
      return {
        ...state,
        checkout: {
          ...state.checkout,
          processing: false,
          success: false,
          error: true,
          errorMessage: action.payload || 'Payment failed'
        }
      }

    default:
      return state
  }
}
